import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
    Box, Button, TextField, MenuItem, Autocomplete, Checkbox, 
    FormControlLabel, Typography, IconButton, Chip, Tooltip, TableRow, TableCell, Snackbar, Alert
} from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import PaidIcon from '@mui/icons-material/Paid';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TuneIcon from '@mui/icons-material/Tune';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import * as API from '../../api';
import DataTable from '../DataTable';
import CustomDatePicker from '../CustomDatePicker';
import ConfirmDialog from '../ConfirmDialog';
import BulkPaymentDialog from '../BulkPaymentDialog';
import BillDetailDialog from '../BillDetailDialog';
import { formatCurrency, parseCurrency } from '../../utils/currencyUtils';
import { formatDateDisplay } from '../../utils/dateUtils';
import ReportExportDialog from './ReportExportDialog';

// --- CONSTANTS ---
export const EXPENSE_CATEGORIES = {
    'OFFICE_RENT': 'Thuê văn phòng',
    'STATIONERY': 'Văn phòng phẩm',
    'UTILITIES': 'Điện nước',
    'SALARY': 'Lương/ Thưởng',
    'SUPPLIES_EQUIPMENT': 'Đồ dùng & Thiết bị',
    'INSURANCE': 'Bảo hiểm',
    'INTERNET': 'Internet',
    'MARKETING': 'Quảng cáo MKT',
    'MANAGEMENT_FEE': 'CP Quản lý',
    'OTHER': 'Khác',
};

const CustomLabel = ({ title }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title}
    </Typography>
);

const GeneralExpensesTab = forwardRef((props, ref) => {
    const [searchParams] = useSearchParams();

    // --- STATE BỘ LỌC ---
    const [uiStartDate, setUiStartDate] = useState(
        searchParams.get('start') || format(startOfMonth(new Date()), 'yyyy-MM-dd')
    );
    const [uiEndDate, setUiEndDate] = useState(
        searchParams.get('end') || format(endOfMonth(new Date()), 'yyyy-MM-dd')
    );
    const [uiIsAllTime, setUiIsAllTime] = useState(
        searchParams.get('all_time') === 'true'
    );
    const [uiExpenseCategory, setUiExpenseCategory] = useState('ALL');
    const [uiExpenseCreator, setUiExpenseCreator] = useState('ALL');
    const [userOptions, setUserOptions] = useState([]);
    const [uiHasAmortization, setUiHasAmortization] = useState('ALL'); // ALL, YES, NO
    const [uiPaymentStatus, setUiPaymentStatus] = useState('ALL');     // ALL, PAID, UNPAID
    const [uiDistributionStatus, setUiDistributionStatus] = useState('ALL'); // ALL, DONE, PENDING

    // --- STATE DỮ LIỆU & UI ---
    const [expenseData, setExpenseData] = useState([]);
    const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
    
    // --- STATE MODALS ---
    const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
    const [bulkPayModal, setBulkPayModal] = useState({ open: false, items: [], type: 'EXPENSE_PAYMENT' });
    const [viewBillModal, setViewBillModal] = useState({ open: false, billData: null });
    const [adjustModal, setAdjustModal] = useState({ open: false, row: null, newMonths: 0 });

    const [reportOpen, setReportOpen] = useState(false);

    const showToast = (msg, severity = 'success') => setToast({ open: true, message: msg, severity });

    // --- INITIALIZATION ---
    useEffect(() => {
        if (props.setCanExport) props.setCanExport(true);
        
        // Load User Options
        API.client.get('/users/').then(res => setUserOptions(res.data)).catch(console.error);
        
        loadData();
    }, []);

    useImperativeHandle(ref, () => ({
        resetFilters: () => {
            const defaultStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
            const defaultEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

            setUiStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
            setUiEndDate(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
            setUiIsAllTime(false);
            setUiExpenseCategory('ALL');
            setUiExpenseCreator('ALL');
            setUiHasAmortization('ALL');
            setUiPaymentStatus('ALL');
            setUiDistributionStatus('ALL');
            setSelectedExpenseIds([]);

            loadData({ 
                isAllTime: false,
                startDate: defaultStart,
                endDate: defaultEnd,
                category: 'ALL',
                created_by: 'ALL',
                has_amortization: 'ALL',
                payment_status: 'ALL',
                distribution_status: 'ALL'
            });
            const params = {
                start_date: defaultStart,
                end_date: defaultEnd
            };
        },
        loadData: () => loadData(),
        openExportDialog: () => setReportOpen(true)
    }));

    // --- LOAD DATA ---
    const loadData = async (overrides = {}) => {
        try {
            const isAllTime = overrides.hasOwnProperty('isAllTime') ? overrides.isAllTime : uiIsAllTime;
            const start = overrides.startDate || uiStartDate;
            const end = overrides.endDate || uiEndDate;
            
            const category = overrides.hasOwnProperty('category') ? overrides.category : uiExpenseCategory;
            const creator = overrides.hasOwnProperty('created_by') ? overrides.created_by : uiExpenseCreator;
            const hasAmort = overrides.hasOwnProperty('has_amortization') ? overrides.has_amortization : uiHasAmortization;
            const payStatus = overrides.hasOwnProperty('payment_status') ? overrides.payment_status : uiPaymentStatus;
            const distStatus = overrides.hasOwnProperty('distribution_status') ? overrides.distribution_status : uiDistributionStatus;

            const params = {};
            if (!isAllTime) {
                params.start_date = start;
                params.end_date = end;
            }
            if (category !== 'ALL') params.category = category;
            if (creator !== 'ALL') params.created_by = creator;
            if (hasAmort !== 'ALL') params.has_amortization = hasAmort;
            if (payStatus !== 'ALL') params.payment_status = payStatus;
            if (distStatus !== 'ALL') params.distribution_status = distStatus;

            const res = await API.client.get('/general-expenses/', { params });
            setExpenseData(res.data);
        } catch (error) {
            console.error(error);
            showToast("Lỗi tải dữ liệu chi phí", "error");
        }
    };

    const analyzeExpenseDistribution = (selectedRows) => {
        let summary = {
            totalRevenue: 0,
            totalCost: 0,
            totalProfit: 0,
            payers: {},
            companyPaidAmount: 0 // Thay isPaidByCompany bằng số tiền thực chi
        };

        selectedRows.forEach(row => {
            const amt = parseFloat(row.amount) || 0;
            summary.totalCost += amt;

            if (row.payment_status === 'PAID') {
                if (row.payer_id) {
                    if (!summary.payers[row.payer_id]) {
                        summary.payers[row.payer_id] = { 
                            name: row.created_by_name || 'Cổ đông', 
                            paid: 0, 
                            received: 0 
                        };
                    }
                    summary.payers[row.payer_id].paid += amt;
                } else {
                    // Nếu không có payer_id, mặc định là Công ty đã chi trả
                    summary.companyPaidAmount += amt;
                }
            }
        });
        return summary;
    };

    // --- HANDLERS ---
    const handleAddNewRow = () => {
        const currentUser = localStorage.getItem('user_display_name') || 'Admin';
        const newRow = {
            id: `new_${Date.now()}`,
            date: new Date().toISOString().slice(0, 10),
            category: 'OTHER',
            amount: '',
            start_month: null, 
            end_month: null,
            note: '',
            created_by_name: currentUser,
            payment_status: 'UNPAID',
            distribution_status: 'PENDING',
            isNew: true
        };
        setExpenseData([newRow, ...expenseData]);
    };

    const handleRowChange = (id, field, value) => {
        setExpenseData(prev => prev.map(row => {
            if (row.id !== id) return row;

            let updates = { [field]: value, isModified: true };

            if (field === 'start_month' || field === 'end_month') {
                const start = field === 'start_month' ? value : row.start_month;
                const end = field === 'end_month' ? value : row.end_month;

                if (start && end && new Date(end) < new Date(start)) {
                    showToast("Ngày kết thúc không được nhỏ hơn ngày bắt đầu", "warning");
                }
            }

            return { ...row, ...updates };
        }));
    };

    const handleSaveAllExpenses = async () => {
        const rowsToProcess = expenseData.filter(row => row.isNew || row.isModified);
        if (rowsToProcess.length === 0) return showToast("Không có thay đổi nào để lưu", "info");

        try {
            let successCount = 0;
            for (const row of rowsToProcess) {
                if (!row.amount || parseFloat(row.amount) <= 0) continue;

                const payload = {
                    date: row.date,
                    category: row.category,
                    amount: row.amount,
                    start_month: row.start_month || null,
                    end_month: row.end_month || null,
                    note: row.note
                };

                if (row.isNew) {
                    await API.client.post('/general-expenses/', payload);
                    successCount++;
                } else if (row.payment_status !== 'PAID') {
                    await API.client.patch(`/general-expenses/${row.id}/`, payload);
                    successCount++;
                }
            }
            if (successCount > 0) {
                showToast(`Đã lưu ${successCount} mục`, "success");
                loadData();
            }
        } catch (e) {
            showToast("Lỗi khi lưu dữ liệu", "error");
        }
    };

    const handleDeleteClick = (row) => {
        if (row.isNew) {
            setExpenseData(prev => prev.filter(r => r.id !== row.id));
        } else if (row.payment_status === 'PAID') {
            showToast("Chi phí đã thanh toán, không thể xóa!", "warning");
        } else {
            setConfirmDelete({ open: true, id: row.id });
        }
    };

    const confirmDeleteAction = async () => {
        if (!confirmDelete.id) return;
        try {
            await API.deleteGeneralExpense(confirmDelete.id);
            showToast("Đã xóa chi phí", "success");
            loadData();
        } catch (e) { showToast("Lỗi khi xóa", "error"); }
        finally { setConfirmDelete({ open: false, id: null }); }
    };

    const handlePayExpense = () => {
        const selectedRows = expenseData.filter(r => selectedExpenseIds.includes(r.id));
        setBulkPayModal({
            open: true,
            items: selectedRows,
            type: 'EXPENSE_PAYMENT',
            distributionSummary: null
        });
    };

    const handleDistributeExpense = () => {
        const selectedRows = expenseData.filter(r => selectedExpenseIds.includes(r.id));
        if (selectedRows.length === 0) return;

        const summary = analyzeExpenseDistribution(selectedRows);
        
        setBulkPayModal({
            open: true,
            items: selectedRows, // Items này đã có field 'id' là expense id
            type: 'EXPENSE_DISTRIBUTION',
            distributionSummary: summary
        });
    };
    
    const handleViewBill = (row, type) => {
        if (type === 'EXTERNAL') {
            setViewBillModal({
                open: true,
                billData: {
                    type: `CHI PHÍ: ${row.category_display || row.category}`,
                    amount: row.amount,
                    note: row.payment_note || '',
                    imageUrl: row.bill_image,
                    supplierName: null,
                    receiver_info: row.receiver_info || row.note || '---',
                    status: 'PAID',
                    transaction_date: row.date || row.created_at,
                    payment_account_name: row.payment_account_name
                }
            });
        } else if (type === 'INTERNAL') {
            let transactions = [];
            let snapshot = null;
            
            if (row.distribution_session && row.distribution_session.transactions) {
                transactions = row.distribution_session.transactions;
                snapshot = row.distribution_session.shareholder_snapshot || null;
            } else if (row.distributions) {
                transactions = row.distributions;
            }

            if (!transactions || transactions.length === 0) {
                return showToast("Không tìm thấy thông tin phân bổ", "warning");
            }

            const savedTransactions = transactions.map(d => {
                const amountVal = parseFloat(d.amount);
                const absAmount = Math.abs(amountVal);
                
                let senderObj = { type: 'UNKNOWN', label: '---', id: null };
                if (d.sender_account) {
                    senderObj = { type: 'COMPANY', label: d.sender_name || `TK Cty ${d.sender_account}`, id: d.sender_account };
                } else if (d.sender_shareholder) {
                    senderObj = { type: 'SHAREHOLDER', label: d.sender_name || `CĐ ${d.sender_shareholder}`, id: d.sender_shareholder };
                }

                let receiverObj = { type: 'UNKNOWN', label: '---', id: null };
                if (d.receive_account) {
                    receiverObj = { type: 'COMPANY', label: d.receiver_name || `TK Cty ${d.receive_account}`, id: d.receive_account };
                } else if (d.receiver_shareholder) {
                    receiverObj = { type: 'SHAREHOLDER', label: d.receiver_name || `CĐ ${d.receiver_shareholder}`, id: d.receiver_shareholder };
                }

                return {
                    id: d.id,
                    sender: senderObj,
                    receiver: receiverObj,
                    amount: absAmount, 
                    note: d.note,
                    fileUrl: d.bill_image || d.transfer_bill, 
                    senderLabel: senderObj.label,
                    receiverLabel: receiverObj.label,
                    transactionDate: d.created_at
                };
            });
            
            const summary = analyzeExpenseDistribution([row]);

            setBulkPayModal({
                open: true,
                items: [row],
                type: 'EXPENSE_DISTRIBUTION',
                distributionSummary: summary,
                readOnly: true,
                savedTransactions: savedTransactions,
                snapshotData: snapshot
            });
        }
    };

    // --- TABLE COLUMNS & RENDER ---
    const columns = [
        {
            id: 'select', align: 'center', minWidth: 50,
            label: <Checkbox size="small" 
                checked={expenseData.length > 0 && selectedExpenseIds.length === expenseData.length}
                indeterminate={selectedExpenseIds.length > 0 && selectedExpenseIds.length < expenseData.length}
                onChange={(e) => setSelectedExpenseIds(e.target.checked ? expenseData.map(r => r.id) : [])} 
            />,
            render: (row) => <Checkbox size="small" checked={selectedExpenseIds.includes(row.id)} onChange={() => 
                setSelectedExpenseIds(prev => prev.includes(row.id) ? prev.filter(x => x !== row.id) : [...prev, row.id])} 
            />
        },
        { id: 'date', label: 'Ngày chi', minWidth: 100, format: (v) => <Typography variant="body2">{formatDateDisplay(v)}</Typography> },
        { id: 'category', label: 'Loại chi phí', minWidth: 200 },
        { id: 'note', label: 'Nội dung', minWidth: 250 },
        { id: 'amortization', label: 'Khấu hao', align: 'center', minWidth: 100 },
        { id: 'amount', label: 'Số tiền (VND)', align: 'right', minWidth: 120 },
        { id: 'created_by', label: 'Người nhập', minWidth: 150 },
        { id: 'status', label: 'TT', align: 'center', minWidth: 80 },
        { id: 'payment', label: 'Bill', align: 'center', minWidth: 60 },
        { id: 'distribution', label: 'Phân bổ', align: 'center', minWidth: 60 },
        { id: 'action', label: 'Xóa', align: 'center', minWidth: 60 }
    ];

    const reportColumns = [
        { 
            header: 'Ngày chi', 
            width: 9, 
            accessor: 'date', 
            render: r => formatDateDisplay(r.date) 
        },
        { 
            header: 'Loại', 
            width: 14, 
            accessor: 'category', 
            render: r => EXPENSE_CATEGORIES[r.category] || r.category 
        },
        { 
            header: 'Nội dung', 
            width: 18, 
            accessor: 'note' 
        },
        { 
            header: 'Tổng tiền', 
            width: 11, 
            accessor: 'amount', 
            render: r => formatCurrency(r.amount) 
        },
        {
            header: 'TG Khấu hao',
            width: 12,
            accessor: 'amortization_period',
            render: (r) => {
                if (r.start_month && r.end_month) {
                    return `${r.start_month.slice(0, 7)} -> ${r.end_month.slice(0, 7)}`;
                }
                return '-';
            }
        },
        {
            header: 'Đã KH',
            width: 9,
            accessor: 'accumulated_depreciation',
            render: (r) => r.start_month ? formatCurrency(r.accumulated_depreciation) : '-'
        },
        {
            header: '/Tháng',
            width: 9,
            accessor: 'monthly_amount',
            render: (r) => r.start_month ? formatCurrency(r.monthly_amount) : '-'
        },
        { 
            header: 'TT', 
            width: 9, 
            accessor: 'payment_status', 
            render: r => r.payment_status === 'PAID' ? 'Đã TT' : 'Chưa' 
        },
        { 
            header: 'Phân bổ', 
            width: 9, 
            accessor: 'distribution_status', 
            render: r => r.distribution_status === 'DONE' ? 'Đã PB' : 'Chưa' 
        }
    ];

    const renderRow = (row) => {
        const isPaid = row.payment_status === 'PAID';
        const isDistributed = row.distribution_status === 'DONE';
        const disabledBg = isPaid ? '#f5f5f5' : 'white';
        
        // Tính toán hiển thị
        const accumulated = parseFloat(row.accumulated_depreciation) || 0;
        const total = parseFloat(row.amount) || 0;
        const percent = total > 0 ? Math.min(100, Math.round((accumulated / total) * 100)) : 0;
        const monthlyAmt = parseFloat(row.monthly_amount) || 0;

        const startVal = row.start_month || '';
        const endVal = row.end_month || '';        

        return (
            <TableRow key={row.id} hover selected={selectedExpenseIds.includes(row.id)}
                sx={{ bgcolor: !isPaid ? '#fffde7' : 'inherit' }}
            >
                <TableCell align="center">
                    <Checkbox size="small" checked={selectedExpenseIds.includes(row.id)} 
                        onChange={() => setSelectedExpenseIds(prev => prev.includes(row.id) ? prev.filter(x => x !== row.id) : [...prev, row.id])} 
                        sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' } }}
                    />
                </TableCell>
                <TableCell><Typography variant="body2">{row.created_at ? format(new Date(row.date), 'dd/MM/yyyy') : '---'}</Typography></TableCell>
                <TableCell>
                    <TextField select fullWidth size="small" value={row.category} disabled={isPaid}
                        onChange={(e) => handleRowChange(row.id, 'category', e.target.value)} sx={{ bgcolor: disabledBg }}
                    >
                        {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                    </TextField>
                </TableCell>
                <TableCell>
                    <TextField fullWidth size="small" value={row.note || ''} placeholder="Nhập nội dung..." disabled={isPaid}
                        onChange={(e) => handleRowChange(row.id, 'note', e.target.value)} sx={{ bgcolor: disabledBg }}
                    />
                </TableCell>
                <TableCell align="center" sx={{ width: 220 }}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" gap={1}>
                            <TextField 
                                type="month"
                                size="small" 
                                value={startVal} // Giá trị '2024-02' hiển thị trực tiếp được trên type="month"
                                disabled={isPaid}
                                onChange={(e) => handleRowChange(row.id, 'start_month', e.target.value)} // Gửi thẳng '2024-02' đi
                                inputProps={{ max: endVal }} 
                                sx={{ bgcolor: disabledBg, '& input': { fontSize: '0.75rem', p: 0.5 } }}
                                FormHelperTextProps={{sx:{m:0, fontSize:'0.6rem'}}}
                            />

                            <TextField 
                                type="month"
                                size="small" 
                                value={endVal}
                                disabled={isPaid}
                                onChange={(e) => handleRowChange(row.id, 'end_month', e.target.value)} // Gửi thẳng '2024-02' đi
                                inputProps={{ min: startVal }}
                                sx={{ bgcolor: disabledBg, '& input': { fontSize: '0.75rem', p: 0.5 } }}
                                FormHelperTextProps={{sx:{m:0, fontSize:'0.6rem'}}}
                            />
                        </Box>
                        {(row.start_month && row.end_month) && (
                            <Box sx={{ width: '100%', mr: 1 }}>
                                <Box sx={{ height: 4, bgcolor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box sx={{ width: `${percent}%`, height: '100%', bgcolor: isPaid ? '#4caf50' : '#ff9800' }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.65rem'}}>
                                    Đã KH: {formatCurrency(accumulated)} ({percent}%)
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </TableCell>

                <TableCell align="right" >
                    <TextField fullWidth size="small" value={row.amount ? formatCurrency(row.amount) : ''} placeholder="0" disabled={isPaid}
                        onChange={(e) => handleRowChange(row.id, 'amount', parseCurrency(e.target.value))}
                        sx={{ bgcolor: disabledBg, width: '120px', '& .MuiInputBase-input': { textAlign: 'right', fontWeight: 'bold', color: isPaid ? 'rgba(0,0,0,0.38)' : '#d32f2f' } }}
                    />
                    {(row.start_month && row.end_month) && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            ~{formatCurrency(monthlyAmt)} /tháng
                        </Typography>
                    )}
                </TableCell>
                <TableCell><Typography variant="body2" noWrap>{row.created_by_name || '---'}</Typography></TableCell>
                <TableCell align="center">
                    <Chip label={isPaid ? "Đã TT" : "Chưa TT"} color={isPaid ? "success" : "default"} size="small" variant="outlined" sx={{fontSize:'0.7rem'}}/>
                </TableCell>
                <TableCell align="center">
                    <IconButton size="small" color={isPaid ? "success" : "default"} disabled={!isPaid} onClick={() => handleViewBill(row, 'EXTERNAL')}>
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </TableCell>
                <TableCell align="center">
                    <IconButton size="small" color={isDistributed ? "info" : "default"} disabled={!isDistributed} onClick={() => handleViewBill(row, 'INTERNAL')}>
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </TableCell>
                <TableCell align="center">
                    <IconButton size="small" color="error" disabled={isPaid} onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </TableCell>
            </TableRow>
        );
    };

    const hasPaidItem = expenseData.filter(r => selectedExpenseIds.includes(r.id)).some(r => r.payment_status === 'PAID');
    const hasDistributedItem = expenseData.filter(r => selectedExpenseIds.includes(r.id)).some(r => r.distribution_status === 'DONE');

    const totalPaidAmount = expenseData
        .filter(item => item.payment_status === 'PAID')
        .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

    const totalUnpaidAmount = expenseData
        .filter(item => item.payment_status !== 'PAID')
        .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

    return (
        <Box>
            {/* FILTERS */}
            <Box sx={{ mb: 2 }}>
                {/* HÀNG 1: THỜI GIAN & NGƯỜI NHẬP & NÚT ÁP DỤNG */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
                    <Box sx={{ paddingTop: '28px' }}>
                        <FormControlLabel 
                            control={<Checkbox checked={uiIsAllTime} onChange={e => setUiIsAllTime(e.target.checked)} size="small" sx={{ color: '#007373', '&.Mui-checked': { color: '#007373' } }} />} 
                            label={<Typography variant="body2" fontWeight="bold">Toàn thời gian</Typography>} 
                            sx={{ mr: 0 }}
                        />
                    </Box>
                    
                    <Box sx={{ width: '200px' }}>
                        <CustomLabel title="Từ ngày" />
                        <CustomDatePicker value={uiStartDate} onChange={e => setUiStartDate(e.target.value)} disabled={uiIsAllTime} />
                    </Box>
                    
                    <Box sx={{ width: '200px' }}>
                        <CustomLabel title="Đến ngày" />
                        <CustomDatePicker value={uiEndDate} onChange={e => setUiEndDate(e.target.value)} disabled={uiIsAllTime} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                        <CustomLabel title="Người nhập" />
                        <Autocomplete
                            size="small"
                            sx={{ bgcolor: 'white' }}
                            options={[{ id: 'ALL', username: 'Tất cả' }, ...userOptions.filter(u => u.username !== 'admin')]}
                            getOptionLabel={(opt) => {
                                if (opt.id === 'ALL') return 'Tất cả';
                                return `${opt.last_name || ''} ${opt.first_name || ''}`.trim() || opt.username;
                            }}
                            value={userOptions.find(u => u.id === uiExpenseCreator) || { id: 'ALL', username: 'Tất cả' }}
                            onChange={(e, val) => setUiExpenseCreator(val ? val.id : 'ALL')}
                            renderInput={(params) => <TextField {...params} />}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                        />
                    </Box>

                    <Box sx={{ marginTop: '27px' }}>
                        <Button variant="contained" onClick={() => loadData()} sx={{ bgcolor: '#007373', fontWeight: 'bold', height: 40, px: 3, width: '200px' }}>
                            Áp dụng
                        </Button>
                    </Box>
                </Box>

                {/* HÀNG 2: CÁC BỘ LỌC PHÂN LOẠI (DÀN ĐỀU) */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', width: '100%' }}>
                    <Box sx={{ width: '200px' }}>
                        <CustomLabel title="Loại chi phí" />
                        <TextField select fullWidth size="small" value={uiExpenseCategory} onChange={(e) => setUiExpenseCategory(e.target.value)} sx={{ bgcolor: 'white' }}>
                            <MenuItem value="ALL">-- Tất cả --</MenuItem>
                            {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                        </TextField>
                    </Box>

                    <Box sx={{ width: '200px' }}>
                        <CustomLabel title="Trạng thái Khấu hao" />
                        <TextField select fullWidth size="small" value={uiHasAmortization} onChange={(e) => setUiHasAmortization(e.target.value)} sx={{ bgcolor: 'white' }}>
                            <MenuItem value="ALL">-- Tất cả --</MenuItem>
                            <MenuItem value="YES">Có khấu hao</MenuItem>
                            <MenuItem value="NO">Không khấu hao</MenuItem>
                        </TextField>
                    </Box>

                    <Box sx={{ width: '200px' }}>
                        <CustomLabel title="TT Thanh toán" />
                        <TextField select fullWidth size="small" value={uiPaymentStatus} onChange={(e) => setUiPaymentStatus(e.target.value)} sx={{ bgcolor: 'white' }}>
                            <MenuItem value="ALL">-- Tất cả --</MenuItem>
                            <MenuItem value="PAID">Đã thanh toán</MenuItem>
                            <MenuItem value="UNPAID">Chưa thanh toán</MenuItem>
                        </TextField>
                    </Box>

                    <Box sx={{ width: '200px' }}>
                        <CustomLabel title="TT Phân bổ" />
                        <TextField select fullWidth size="small" value={uiDistributionStatus} onChange={(e) => setUiDistributionStatus(e.target.value)} sx={{ bgcolor: 'white' }}>
                            <MenuItem value="ALL">-- Tất cả --</MenuItem>
                            <MenuItem value="DONE">Đã phân bổ</MenuItem>
                            <MenuItem value="PENDING">Chưa phân bổ</MenuItem>
                        </TextField>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', gap: 2, justifyContent: 'flex-end', pb: 0.5 }}>
                        <Box sx={{ bgcolor: '#ffebee', px: 2, py: 1, borderRadius: 1, border: '1px solid #ffcdd2', textAlign: 'center' }}>
                            <Typography variant="caption" color="error.main" fontWeight="bold">CHƯA THANH TOÁN</Typography>
                            <Typography variant="body2" fontWeight="bold" color="error.dark">{formatCurrency(totalUnpaidAmount)}</Typography>
                        </Box>
                        <Box sx={{ bgcolor: '#e8f5e9', px: 2, py: 1, borderRadius: 1, border: '1px solid #c8e6c9', textAlign: 'center' }}>
                            <Typography variant="caption" color="success.main" fontWeight="bold">ĐÃ THANH TOÁN</Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.dark">{formatCurrency(totalPaidAmount)}</Typography>
                        </Box>
                    </Box>   
                </Box>
            </Box>

            {/* ACTION BAR */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" color="primary" disabled={selectedExpenseIds.length === 0 || hasPaidItem} onClick={handlePayExpense} startIcon={<PaidIcon />}>
                        Thanh toán ({selectedExpenseIds.length})
                    </Button>
                    <Button variant="outlined" color="secondary" disabled={selectedExpenseIds.length === 0 || hasDistributedItem} onClick={handleDistributeExpense} startIcon={<GroupAddIcon />}>
                        Phân bổ ({selectedExpenseIds.length})
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="contained" sx={{ bgcolor: '#007373' }} onClick={handleAddNewRow}>+ Thêm Chi phí</Button>
                    <Button variant="contained" color="success" onClick={handleSaveAllExpenses} startIcon={<SaveIcon />}>Lưu Dữ Liệu</Button>
                </Box>
            </Box>

            <DataTable columns={columns} data={expenseData} renderRow={renderRow} noDataText="Chưa có dữ liệu chi phí" />

            {/* DIALOGS */}
            <BulkPaymentDialog 
                open={bulkPayModal.open}
                onClose={() => setBulkPayModal({ ...bulkPayModal, open: false })}
                items={bulkPayModal.items}
                type={bulkPayModal.type}
                distributionSummary={bulkPayModal.distributionSummary}
                readOnly={bulkPayModal.readOnly}
                savedTransactions={bulkPayModal.savedTransactions}
                showToast={showToast}
                onSuccess={() => { setSelectedExpenseIds([]); loadData(); }}
            />
            <BillDetailDialog open={viewBillModal.open} onClose={() => setViewBillModal({ ...viewBillModal, open: false })} billData={viewBillModal.billData} />
            <ConfirmDialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null })} onConfirm={confirmDeleteAction} title="Xác nhận xóa" content="Xóa chi phí này?" />
            {(() => {
                const totalAmount = expenseData.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
                const summaryRowData = {
                    date: 'TỔNG CỘNG:', // Khớp với accessor của cột đầu tiên
                    amount: formatCurrency(totalAmount)
                };

                return (
                    <ReportExportDialog 
                        open={reportOpen} 
                        onClose={() => setReportOpen(false)} 
                        customColumns={reportColumns} 
                        summaryRow={summaryRowData} /* <--- THÊM DÒNG TỔNG */
                        fileNamePrefix="BaoCao_ChiPhi" 
                        customTitle="BÁO CÁO CHI PHÍ CHUNG" 
                        data={expenseData} 
                        dateRange={{ start: uiStartDate, end: uiEndDate }} 
                    />
                );
            })()}
            <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast({...toast, open:false})} anchorOrigin={{vertical:'top', horizontal:'right'}}><Alert severity={toast.severity}>{toast.message}</Alert></Snackbar>
        </Box>
    );
});

export default GeneralExpensesTab;